import { useState } from "react";

import { Card } from "../Card";
import { FormField, inputStyle } from "../ui/FormField";
import { ConsentAttestationField, StageBadge } from "./ConsentAttestationField";
import type { StageTone } from "./ConsentAttestationField";
import type { ConsentAttestation, EngagementRecord } from "../../api/engagements";
import type { ConsentStage, DrillStage } from "../../lib/videoDrill";
import { ApiError } from "../../lib/api";
import { useUpdateEngagement } from "../../hooks/useEngagement";

// ─── Consent stage → badge mapping ───────────────────────────────────────────

interface StageMeta {
  label: (filled: number) => string;
  tone: StageTone;
}

const CONSENT_STAGE_META: Record<ConsentStage, StageMeta> = {
  none: { label: () => "Not started", tone: "muted" },
  partial: { label: (n) => `Incomplete (${n} of 3)`, tone: "warning" },
  complete: { label: () => "Complete", tone: "success" },
};

// ─── Button styles ────────────────────────────────────────────────────────────

const btnBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "var(--radius-input)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 14px",
  minHeight: 36,
  border: "1px solid transparent",
};

const primaryBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--accent)",
  color: "#FFFBF4",
  borderColor: "var(--accent)",
};

const primaryBtnDisabled: React.CSSProperties = {
  ...btnBase,
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

// ─── VideoConsentCard ─────────────────────────────────────────────────────────

interface Props {
  engagementId: string;
  rec: EngagementRecord;
  stage: DrillStage;
}

export function VideoConsentCard({ engagementId, rec, stage }: Props) {
  const vcon = rec.video_consent;

  const [likeness, setLikeness] = useState<ConsentAttestation | null>(
    vcon?.likeness_release ?? null,
  );
  const [sponsor, setSponsor] = useState<ConsentAttestation | null>(
    vcon?.org_sponsor_signoff ?? null,
  );
  const [vendor, setVendor] = useState<ConsentAttestation | null>(
    vcon?.vendor_consent_record ?? null,
  );
  const [retentionDays, setRetentionDays] = useState<number | null>(
    vcon?.biometric_retention_days ?? null,
  );
  const [err, setErr] = useState<string | null>(null);

  const update = useUpdateEngagement(engagementId);

  function save() {
    setErr(null);
    update.mutate(
      {
        video_consent: {
          likeness_release: likeness,
          org_sponsor_signoff: sponsor,
          vendor_consent_record: vendor,
          biometric_retention_days: retentionDays,
        },
      },
      {
        onError: (e) => {
          setErr(e instanceof ApiError ? e.detail : "Save failed");
        },
      },
    );
  }

  // Count filled attestations for the partial label
  const filledCount = [likeness, sponsor, vendor].filter(
    (a) => a && a.sha256 && a.signed_by && a.signed_at,
  ).length;

  const stageMeta = CONSENT_STAGE_META[stage.consent];
  const badgeLabel = stageMeta.label(filledCount);

  return (
    <Card title="" accentColor="var(--warning)">
      {/* Custom header row with title + badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 12,
          borderBottom: "1px solid var(--border-subtle)",
          marginBottom: -2,
        }}
      >
        <h5
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: "var(--text-primary)",
          }}
        >
          Consent
        </h5>
        <StageBadge label={badgeLabel} tone={stageMeta.tone} />
      </div>

      {/* Attestation fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <ConsentAttestationField
          label="Likeness release"
          value={likeness}
          onChange={setLikeness}
        />

        <ConsentAttestationField
          label="Org-sponsor sign-off"
          value={sponsor}
          onChange={setSponsor}
        />

        <ConsentAttestationField
          label="Vendor consent record"
          value={vendor}
          onChange={setVendor}
        />

        <FormField label="Biometric retention (days)">
          <input
            type="number"
            style={inputStyle}
            value={retentionDays ?? ""}
            placeholder="e.g. 365"
            min={1}
            onChange={(e) =>
              setRetentionDays(e.target.value ? Number(e.target.value) : null)
            }
          />
        </FormField>

        {/* Privacy note */}
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: "var(--text-muted)",
            fontFamily: "var(--font-sans)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
          }}
        >
          The platform stores only the hash, signer, and date — never the document
          or the biometric data.
        </p>

        {/* Error */}
        {err ? (
          <div
            style={{ color: "var(--error)", fontSize: 12, fontFamily: "var(--font-sans)" }}
          >
            {err}
          </div>
        ) : null}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: 12,
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          <button
            onClick={save}
            disabled={update.isPending}
            style={update.isPending ? primaryBtnDisabled : primaryBtn}
          >
            {update.isPending ? "SAVING…" : "SAVE CONSENT"}
          </button>
        </div>
      </div>
    </Card>
  );
}
